import type { FloorSheetMisfireRecord } from './floorSheetCrcFields'
import type { FloorSheetOperatorRow } from './floorSheetOperatorRows'
import { normalizeEquipmentToken } from './floorSheetOperatorRows'
import {
  expandPassSequenceToLabels,
  legacyWeldDetailsGroupKey,
  passSectionInputForBevel,
  weldDetailsGroupStorageKey
} from './passSequenceExpanded'

export function passLabelsIncludeRootMisfire(labels: string[]): boolean {
  return labels.some((l) => l === 'Root Misfire' || /^Root Misfire \d+$/.test(l))
}

export function isRootMisfireDepositionPass(pass: string): boolean {
  return pass === 'Root Misfire' || /^Root Misfire \d+$/.test(pass)
}

export function filterDepositionPassLabels(labels: string[]): string[] {
  return labels.filter((p) => !isRootMisfireDepositionPass(p))
}

/**
 * When Root Misfire is in the pass sequence, the PQR Welding Variables table
 * shows a single "Root Misfire" column (sourced from the numbered misfire pass)
 * instead of "Root", and drops duplicate Root Misfire pass columns.
 * Fill 1..N collapse to "Fills"; Cap 1..N collapse to "Caps".
 */
export type PqrWeldingVariablePassColumn = {
  /** Column header on the PQR Welding Variables matrix. */
  display: string
  /** Pass key used to read `weldDetails.groups`. */
  dataPass: string
  /** When collapsing Fills / Caps, all source pass labels for merging. */
  sourcePasses?: string[]
}

export function resolvePqrWeldingVariablePassColumns(
  labels: string[]
): PqrWeldingVariablePassColumn[] {
  const base = (() => {
    if (!passLabelsIncludeRootMisfire(labels)) {
      return labels.map((label) => ({ display: label, dataPass: label }))
    }
    const misfireDataPass = findRootMisfirePassLabel(labels)
    const out: PqrWeldingVariablePassColumn[] = []
    let rootReplaced = false
    for (const label of labels) {
      if (isRootMisfireDepositionPass(label)) continue
      if (label === 'Root' && !rootReplaced) {
        out.push({ display: 'Root Misfire', dataPass: misfireDataPass })
        rootReplaced = true
        continue
      }
      out.push({ display: label, dataPass: label })
    }
    return out
  })()

  const fillCols = base.filter((c) => /^Fill \d+$/i.test(c.display))
  const capCols = base.filter((c) => /^Cap \d+$/i.test(c.display))
  const collapseFills = fillCols.length >= 2
  const collapseCaps = capCols.length >= 2

  const collapsed: PqrWeldingVariablePassColumn[] = []
  let fillsIdx = -1
  let capsIdx = -1
  for (const col of base) {
    if (/^Fill \d+$/i.test(col.display)) {
      if (!collapseFills) {
        collapsed.push({ ...col, sourcePasses: [col.dataPass] })
        continue
      }
      if (fillsIdx < 0) {
        fillsIdx = collapsed.length
        collapsed.push({ display: 'Fills', dataPass: col.dataPass, sourcePasses: [col.dataPass] })
      } else {
        collapsed[fillsIdx].sourcePasses = [
          ...(collapsed[fillsIdx].sourcePasses ?? []),
          col.dataPass
        ]
      }
      continue
    }
    if (/^Cap \d+$/i.test(col.display)) {
      if (!collapseCaps) {
        collapsed.push({ ...col, sourcePasses: [col.dataPass] })
        continue
      }
      if (capsIdx < 0) {
        capsIdx = collapsed.length
        collapsed.push({
          display: 'Caps',
          dataPass: col.dataPass,
          sourcePasses: [col.dataPass]
        })
      } else {
        collapsed[capsIdx].sourcePasses = [
          ...(collapsed[capsIdx].sourcePasses ?? []),
          col.dataPass
        ]
      }
      continue
    }
    collapsed.push({ ...col, sourcePasses: [col.dataPass] })
  }
  return collapsed
}

/** Map a PQR display pass label to the weld-details navigation target. */
export function resolveWeldingVariableNavigationPass(displayPass: string): string {
  if (displayPass === 'Root Misfire') return 'Root Misfire 1'
  if (displayPass === 'Fills') return 'Fill 1'
  if (displayPass === 'Caps' || displayPass === 'Split Caps') return 'Cap 1'
  return displayPass
}

function asRecord(v: unknown): Record<string, unknown> | null {
  return v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : null
}

function str(v: unknown): string {
  return typeof v === 'string' ? v.trim() : ''
}

export function findRootMisfirePassLabel(labels: string[]): string {
  return (
    labels.find((l) => l === 'Root RC') ??
    labels.find((l) => l === 'Root Misfire 1') ??
    labels.find((l) => l === 'Root Misfire') ??
    labels.find((l) => /^Root Misfire \d+$/.test(l)) ??
    'Root RC'
  )
}

function readWeldDetailFields(
  weldDetails: unknown,
  bevelIndex: number,
  passLabel: string
): Record<string, string> {
  const groups = asRecord(asRecord(weldDetails)?.groups)
  if (!groups) return {}
  const candidates =
    passLabel === 'Root RC' || isRootMisfireDepositionPass(passLabel)
      ? [
          'Root RC',
          'Root Misfire 1',
          'Root Misfire',
          passLabel,
          ...([1, 2, 3].map((n) => `Root Misfire ${n}`))
        ]
      : [passLabel]
  for (const label of Array.from(new Set(candidates))) {
    const composite = weldDetailsGroupStorageKey(bevelIndex, label)
    let state = asRecord(groups[composite])
    if (!state && bevelIndex === 0) {
      state = asRecord(groups[label])
      if (!state) {
        const legacy = legacyWeldDetailsGroupKey(label)
        if (legacy) state = asRecord(groups[legacy])
      }
    }
    if (state) {
      const fields = asRecord(state.fields) ?? {}
      return Object.fromEntries(Object.entries(fields).map(([k, v]) => [k, str(v)]))
    }
  }
  return {}
}

function readSemiAutoWelderName(rows: FloorSheetOperatorRow[]): string {
  const row = rows.find((r) => normalizeEquipmentToken(r.equipment) === 'SEMIAUTOMATICWELDING')
  return row?.name.trim() ?? ''
}

function readMisfirePowerSupply(
  clampSection: unknown,
  passLabels: string[]
): string {
  const clamp = asRecord(clampSection)
  if (!clamp) return ''
  const pps = asRecord(clamp.passPowerSupplies) ?? {}
  const keys = [findRootMisfirePassLabel(passLabels), 'Root Misfire', 'Root Misfire 1']
  for (const key of keys) {
    const v = str(pps[key])
    if (v) return v
  }
  return ''
}

function fillEmpty(current: string, incoming: string): string {
  return current.trim() ? current : incoming.trim()
}

/** When WPS has a value, use it; otherwise keep the floor sheet value. */
function preferSeed(current: string, incoming: string): string {
  return incoming.trim() ? incoming.trim() : current.trim()
}

export type MisfireMetaSeed = Partial<
  Pick<
    FloorSheetMisfireRecord,
    'process' | 'progression' | 'polarity' | 'completedBy' | 'powerSupply' | 'wireFeedSpeed'
  >
>

/** Build suggested misfire footer values from WPS sections (does not mutate). */
export function buildMisfireMetaSeed(
  sections: Record<string, unknown>,
  bevelIndex: number,
  operatorRows: FloorSheetOperatorRow[] = [],
  fsTargetsWfs = ''
): MisfireMetaSeed {
  const passInput = passSectionInputForBevel(sections.pass, bevelIndex)
  const passLabels = expandPassSequenceToLabels(passInput)
  if (!passLabelsIncludeRootMisfire(passLabels)) return {}

  const misfirePass = 'Root RC'
  const fields = readWeldDetailFields(sections.weldDetails, bevelIndex, misfirePass)

  return {
    process: fields.weldingProcess ?? '',
    progression: fields.weldingDirection ?? '',
    polarity: fields.electricalPolarity ?? '',
    completedBy: readSemiAutoWelderName(operatorRows),
    powerSupply: readMisfirePowerSupply(sections.clamp, passLabels),
    wireFeedSpeed: fsTargetsWfs.trim()
  }
}

/** Sync misfire footer meta from WPS / operators / target values. */
export function mergeMisfireMetaPrefill(
  record: FloorSheetMisfireRecord,
  seed: MisfireMetaSeed
): FloorSheetMisfireRecord {
  if (!seed || Object.keys(seed).length === 0) return record
  return {
    ...record,
    process: preferSeed(record.process, seed.process ?? ''),
    progression: preferSeed(record.progression, seed.progression ?? ''),
    polarity: preferSeed(record.polarity, seed.polarity ?? ''),
    completedBy: fillEmpty(record.completedBy, seed.completedBy ?? ''),
    powerSupply: preferSeed(record.powerSupply, seed.powerSupply ?? ''),
    wireFeedSpeed: preferSeed(record.wireFeedSpeed, seed.wireFeedSpeed ?? '')
  }
}
